import React from 'react';
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';
import ModalWrapper from './ModalWrapper';
import { CreateProductModalData } from '../types';

interface CreateProductConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting?: boolean;
  productData: CreateProductModalData;
}

// Safely extracts a string from either a plain string or an APIValueObject
const toStr = (val: unknown): string => {
  if (!val) return '-';
  if (typeof val === 'string') return val || '-';
  if (typeof val === 'object' && 'value' in (val as object))
    return String((val as { value: unknown }).value || '') || '-';
  return '-';
};

const CreateProductConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting = false,
  productData,
}: CreateProductConfirmModalProps) => {

  const renderField = (label: string, value: unknown) => (
    <div className="tw-flex tw-flex-col tw-gap-0.5">
      <Typography variant="caption-metadata" className="tw-text-ink-secondary">
        {label}
      </Typography>
      <Typography variant="sub-title-label" className="tw-text-ink">
        {toStr(value)}
      </Typography>
    </div>
  );

  const renderFooter = () => (
    <div className="tw-flex tw-justify-end">
      <Button variant="primary" onClick={onConfirm} disabled={isSubmitting}>
        {isSubmitting ? 'Saving...' : 'Save Product'}
      </Button>
    </div>
  );

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      title="Create as New Product"
      footer={renderFooter()}
      width="normal"
    >
      <div className="tw-space-y-4">
        {/* Preview heading */}
        <Typography variant="heading-2" className="tw-text-ink">
          Preview
        </Typography>

        {/* Product Name - inline label + value */}
        <div className="tw-flex tw-items-center tw-gap-1">
          <Typography variant="caption-metadata" className="tw-text-ink-secondary tw-shrink-0">
            Product Name:
          </Typography>
          <Typography variant="sub-title-label" className="tw-text-ink">
            {toStr(productData.productName)}
          </Typography>
        </div>

        {/* Category + UID */}
        <div className="tw-grid tw-grid-cols-2 tw-gap-4">
          {renderField('Category', productData.category)}
          {renderField('UID', productData.categoryUid || '-')}
        </div>

        {/* Description */}
        {renderField('Description', productData.description)}

        {/* SKU row - 4 columns */}
        <div className="tw-grid tw-grid-cols-4 tw-gap-4">
          {renderField('SKU', productData.sku)}
          {renderField('Real SKU', productData.realSku)}
          {renderField('Model Number', productData.modelNumber)}
          {renderField('Parent ID', productData.parentId)}
        </div>

        {/* PLS row - 4 columns */}
        <div className="tw-grid tw-grid-cols-4 tw-gap-4">
          {renderField('PLS', productData.pls)}
          {renderField('Price', productData.price)}
          {renderField('Version', productData.version)}
          {renderField('UPC', productData.upc)}
        </div>

        {/* Divider */}
        <div className="tw-border-t tw-border-edge" />

        {/* Attributes */}
        {productData.attributes.filter(a => a.name).length > 0 && (
          <div>
            <Typography variant="sub-title-label" className="tw-text-ink tw-mb-3">
              Attributes
            </Typography>
            <div className="tw-grid tw-grid-cols-3 tw-gap-4">
              {productData.attributes.filter(a => a.name).map((attr, index) => {
                const joined = attr.values.map(v => toStr(v)).filter(v => v !== '-').join(', ') || '-';
                return (
                  // min-w-0 lets the grid cell shrink below its content's
                  // intrinsic width; tw-break-words on the value wraps long
                  // unbroken strings (image URLs, semicolon-joined crumbs)
                  // so they no longer push the row past the modal edge and
                  // trigger a body-level horizontal scrollbar.
                  // `title` goes on the wrapper div (Typography's TS props
                  // don't include a passthrough `title` attribute).
                  <div key={index} className="tw-flex tw-flex-col tw-gap-0.5 tw-min-w-0" title={joined}>
                    <Typography variant="sub-title-label" className="tw-text-ink tw-break-words">
                      {attr.name}
                    </Typography>
                    <Typography variant="body-primary" className="tw-text-ink-secondary tw-break-words">
                      {joined}
                    </Typography>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </ModalWrapper>
  );
};

export default CreateProductConfirmModal;
